import { select } from "d3";

import {
  AppletElement,
  EDGE_STROKE,
  EdgeEndpoints,
  NODE_STROKE,
  SvgSelection,
  renderApplet,
  trimmedEdgeEndpoints,
} from "./common";

type TreeFamilyNode = Readonly<{
  id: string;
  x: number;
  y: number;
}>;

type TreeFamilyEdge = Readonly<{
  source: string;
  target: string;
}>;

type TreeFamilyPanel = Readonly<{
  title: string;
  originX: number;
  nodes: readonly TreeFamilyNode[];
  edges: readonly TreeFamilyEdge[];
}>;

const TRIPTYCH_WIDTH = 720;
const TRIPTYCH_HEIGHT = 190;
const TRIPTYCH_NODE_RADIUS = 7;
const TRIPTYCH_NODE_FILL = "#8fbc8f";
const TRIPTYCH_PANEL_WIDTH = 240;

const TREE_FAMILY_PANELS: readonly TreeFamilyPanel[] = [
  {
    title: "Path",
    originX: 0,
    nodes: [
      { id: "p0", x: 28, y: 96 },
      { id: "p1", x: 62, y: 84 },
      { id: "p2", x: 96, y: 96 },
      { id: "p3", x: 130, y: 84 },
      { id: "p4", x: 164, y: 96 },
      { id: "p5", x: 198, y: 84 },
    ],
    edges: [
      { source: "p0", target: "p1" },
      { source: "p1", target: "p2" },
      { source: "p2", target: "p3" },
      { source: "p3", target: "p4" },
      { source: "p4", target: "p5" },
    ],
  },
  {
    title: "Caterpillar",
    originX: TRIPTYCH_PANEL_WIDTH,
    nodes: [
      { id: "c0", x: 28, y: 104 },
      { id: "c1", x: 68, y: 88 },
      { id: "c2", x: 108, y: 104 },
      { id: "c3", x: 148, y: 88 },
      { id: "c4", x: 188, y: 104 },
      { id: "c5", x: 68, y: 52 },
      { id: "c6", x: 108, y: 136 },
      { id: "c7", x: 148, y: 52 },
      { id: "c8", x: 188, y: 136 },
    ],
    edges: [
      { source: "c0", target: "c1" },
      { source: "c1", target: "c2" },
      { source: "c2", target: "c3" },
      { source: "c3", target: "c4" },
      { source: "c1", target: "c5" },
      { source: "c2", target: "c6" },
      { source: "c3", target: "c7" },
      { source: "c4", target: "c8" },
    ],
  },
  {
    title: "Diameter 5 tree",
    originX: TRIPTYCH_PANEL_WIDTH * 2,
    nodes: [
      { id: "r0", x: 28, y: 112 },
      { id: "r1", x: 64, y: 94 },
      { id: "r2", x: 102, y: 104 },
      { id: "r3", x: 140, y: 88 },
      { id: "r4", x: 176, y: 104 },
      { id: "r5", x: 208, y: 78 },
      { id: "r6", x: 102, y: 50 },
      { id: "r7", x: 132, y: 28 },
    ],
    edges: [
      { source: "r0", target: "r1" },
      { source: "r1", target: "r2" },
      { source: "r2", target: "r3" },
      { source: "r3", target: "r4" },
      { source: "r4", target: "r5" },
      { source: "r2", target: "r6" },
      { source: "r6", target: "r7" },
    ],
  },
];

function treeFamilyLookup(panel: TreeFamilyPanel): ReadonlyMap<string, TreeFamilyNode> {
  return new Map(panel.nodes.map((node) => [node.id, node]));
}

function treeFamilyEdgeEndpoints(edge: TreeFamilyEdge, nodes: ReadonlyMap<string, TreeFamilyNode>): EdgeEndpoints {
  const source = nodes.get(edge.source);
  const target = nodes.get(edge.target);
  if (source === undefined || target === undefined) {
    return { x1: 0, y1: 0, x2: 0, y2: 0 };
  }

  return trimmedEdgeEndpoints(source, target, TRIPTYCH_NODE_RADIUS);
}

function renderTreeFamilyPanel(svg: SvgSelection, panel: TreeFamilyPanel): void {
  const nodes = treeFamilyLookup(panel);
  const panelGroup = svg.append<SVGGElement>("g")
    .attr("class", "tree-family-panel")
    .attr("transform", `translate(${panel.originX},0)`);

  panelGroup.append<SVGGElement>("g")
    .attr("class", "tree-family-edges")
    .selectAll<SVGLineElement, TreeFamilyEdge>("line")
    .data(panel.edges)
    .join("line")
    .attr("x1", (edge: TreeFamilyEdge): number => treeFamilyEdgeEndpoints(edge, nodes).x1)
    .attr("y1", (edge: TreeFamilyEdge): number => treeFamilyEdgeEndpoints(edge, nodes).y1)
    .attr("x2", (edge: TreeFamilyEdge): number => treeFamilyEdgeEndpoints(edge, nodes).x2)
    .attr("y2", (edge: TreeFamilyEdge): number => treeFamilyEdgeEndpoints(edge, nodes).y2)
    .attr("stroke", EDGE_STROKE)
    .attr("stroke-width", 1.2);

  panelGroup.append<SVGGElement>("g")
    .attr("class", "tree-family-nodes")
    .selectAll<SVGCircleElement, TreeFamilyNode>("circle")
    .data(panel.nodes)
    .join("circle")
    .attr("cx", (node: TreeFamilyNode): number => node.x)
    .attr("cy", (node: TreeFamilyNode): number => node.y)
    .attr("r", TRIPTYCH_NODE_RADIUS)
    .attr("fill", TRIPTYCH_NODE_FILL)
    .attr("stroke", NODE_STROKE);

  panelGroup.append<SVGTextElement>("text")
    .attr("class", "tree-family-title")
    .attr("x", TRIPTYCH_PANEL_WIDTH * 0.5)
    .attr("y", TRIPTYCH_HEIGHT - 18)
    .attr("text-anchor", "middle")
    .attr("font-family", "Alegreya, serif")
    .attr("font-size", 18)
    .attr("fill", "#777")
    .text(panel.title);
}

function renderTreeFamilyTriptych(element: AppletElement): void {
  element.dataset.rendered = "true";
  element.replaceChildren();

  const svg = select<AppletElement, unknown>(element)
    .append<SVGSVGElement>("svg")
    .attr("viewBox", `0 0 ${TRIPTYCH_WIDTH} ${TRIPTYCH_HEIGHT}`)
    .attr("width", TRIPTYCH_WIDTH)
    .attr("height", TRIPTYCH_HEIGHT)
    .attr("role", "img")
    .attr("aria-label", "Three unlabeled trees: a path, a caterpillar, and a tree with diameter 5.");

  TREE_FAMILY_PANELS.forEach((panel: TreeFamilyPanel): void => renderTreeFamilyPanel(svg, panel));
}

renderApplet(".tree-family-triptych", renderTreeFamilyTriptych);
